import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useComputedStateFirstNotNull } from 'src/hooks/useComputedState';
import { useEnableSelectionByDefault } from 'src/hooks/useEnableSelectionByDefault';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { ACTIVE_EVENT_IDS_2_QUERY_PARAM } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.constants';
import { EventIdQueryParam } from 'src/utils/constants/constants';

import { MultiSelectScope } from './MultiSelectionContext';
import { SelectionMode } from './MultiSelectionContext.types';

export const useSelectionMode = (
  forceSelectionMode?: SelectionMode,
  initialSelectionMode?: SelectionMode
) => {
  const isMobile = useMatchMedia('mobile');
  const [searchParams] = useSearchParams();
  const enableSelectionByDefault = useEnableSelectionByDefault();
  const defaultSelectionMode = useMemo(
    () => forceSelectionMode ?? initialSelectionMode,
    [forceSelectionMode, initialSelectionMode]
  );

  const activeEventId = useMemo(() => {
    const eventId = searchParams.get(EventIdQueryParam);
    const activeEventId = searchParams.get(ACTIVE_EVENT_IDS_2_QUERY_PARAM);
    return eventId ?? activeEventId;
  }, [searchParams]);

  const defaultSelectionModeWithUserPreference = useMemo(() => {
    if (enableSelectionByDefault && !isMobile) {
      if (activeEventId) {
        return {
          mode: MultiSelectScope.SingleGroup,
          groupId: activeEventId,
        };
      }
      return { mode: MultiSelectScope.AllGroups };
    }
    return defaultSelectionMode;
  }, [activeEventId, defaultSelectionMode, enableSelectionByDefault, isMobile]);

  const [selectionMode, setSelectionMode] = useComputedStateFirstNotNull(
    defaultSelectionModeWithUserPreference,
    defaultSelectionMode,
    true
  );

  const getDefaultSelectionMode = useCallback(
    (groupId?: string) => {
      if (enableSelectionByDefault) {
        const selectionGroupId = groupId ?? activeEventId;
        if (selectionGroupId) {
          return {
            mode: MultiSelectScope.SingleGroup,
            groupId: selectionGroupId,
          };
        }
        return { mode: MultiSelectScope.AllGroups };
      }
      return undefined;
    },
    [activeEventId, enableSelectionByDefault]
  );

  return {
    enableSelectionByDefault,
    selectionMode,
    setSelectionMode,
    getDefaultSelectionMode,
  };
};
